import exceljs from "exceljs";
import { saveAs } from "file-saver";
import type { TableColumnProperties } from "exceljs";

import { mimeTypeMap, MyDate, numberReducer } from "src/components/common/functions";
import type { ImportedBillingProject, ImportedBillingWeekMap } from "./actions_loaders";

const formatWeekLabel = (week: string) => week.replace(/^w/, "").replace("-", "");

export default function Export({
    filteredProjectIds,
    visibleWeeks,
    projectsMap,
    rawData,
}: {
    filteredProjectIds: string[];
    visibleWeeks: string[];
    projectsMap: Record<string, ImportedBillingProject>;
    rawData: ImportedBillingWeekMap;
}) {
    const exportFunc = async () => {
        if (!filteredProjectIds.length) {
            window.alert("No data to export.");
            return;
        }

        const workbook = new exceljs.Workbook();
        workbook.created = new Date();
        workbook.calcProperties.fullCalcOnLoad = true;

        const worksheet = workbook.addWorksheet("Imported Billing", {
            views: [{ showGridLines: false }],
        });
        worksheet.state = "visible";
        worksheet.properties.defaultColWidth = 22.57;

        const rows = filteredProjectIds.map(projectId => {
            const amounts = visibleWeeks.map(
                week => rawData[week]?.[projectId]?.map(item => item.amount).reduce(numberReducer, 0) || 0
            );
            return [
                projectsMap[projectId]?.projectName ?? projectId,
                ...amounts,
                amounts.reduce(numberReducer, 0),
            ];
        });

        const columns: TableColumnProperties[] = [
            { name: "Project Name", filterButton: true },
            ...visibleWeeks.map(week => ({
                name: formatWeekLabel(week),
                filterButton: true,
                totalsRowFunction: "sum" as const,
            })),
            { name: "Billing", filterButton: true, totalsRowFunction: "sum" as const },
        ];

        worksheet.addTable({
            name: "ImportedBillingTable",
            ref: "A1",
            totalsRow: true,
            style: {
                showRowStripes: true,
            },
            columns,
            rows,
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: mimeTypeMap.xlsx }), `Billing export ${(new MyDate()).s()}.xlsx`);
    };

    if (!filteredProjectIds.length) return null;
    return (
        <button type="button" className="btn btn-outline-primary" onClick={exportFunc}>
            Export
        </button>
    );
}
